import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { lt } from '@mews/framework';
import { ConvertedCurrencyValue, LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { OrderItem } from '@mews-commander/types';
import { Tag } from '@optimus-web/core';
import { OrderItemType } from '@mews-commander/data-access';
import { BillLink } from '../../../../../components';
import { additionalExpensesHandler } from '../../additionalExpensesHandler';

export interface TableMeta {
    groupId: string;
    isMultiTaxEnvironment: boolean;
    serviceId: string;
}

const DiscriminatorCell: React.FC<CellContext<OrderItem, OrderItem['data']['discriminator']>> = (info) => {
    const { serviceId } = info.table.options.meta as TableMeta;
    const items = info.table.options.data;
    const orderItem = info.row.original;

    const defaultValue = <>{additionalExpensesHandler(orderItem.data.discriminator, { serviceId })}</>;

    switch (orderItem.data.discriminator) {
        case OrderItemType.AdditionalExpenseRebate: {
            const originalItem = orderItem.data.value.originalItemId;
            const rebatedItem = items.find((item) => item.id === originalItem);

            if (rebatedItem?.data.discriminator === OrderItemType.Deposit) {
                return lt('OrderItemTypeAdditionalExpenseRebateDeposit');
            }
            return defaultValue;
        }
        default:
            return defaultValue;
    }
};

const TotalAmountCell: React.FC<CellContext<OrderItem, OrderItem['totalAmount']>> = (info) => {
    const { isMultiTaxEnvironment } = info.table.options.meta as TableMeta;
    return <ConvertedCurrencyValue amount={info.getValue()} net={isMultiTaxEnvironment} />;
};

const IdCell: React.FC<CellContext<OrderItem, OrderItem['id']>> = (info) => {
    const id = info.getValue();
    const { groupId } = info.table.options.meta as TableMeta;
    return <BillLink orderItemId={id} groupId={groupId} />;
};

const columnHelper = createColumnHelper<OrderItem>();

export const simpleColumns = [
    columnHelper.accessor('data.discriminator', {
        header: () => lt('ExpenseType'),
        cell: DiscriminatorCell,
    }),
    columnHelper.accessor('totalAmount', {
        header: () => lt('Cost'),
        cell: TotalAmountCell,
        meta: {
            align: 'end',
        },
    }),
    columnHelper.accessor('consumedUtc', {
        header: () => lt('Consumed'),
        cell: (info) => <LocalizedDateTimeFromUtc utcString={info.getValue()} />,
    }),
    columnHelper.accessor('id', {
        header: () => lt('Bill'),
        cell: IdCell,
    }),
    columnHelper.accessor('isCanceled', {
        header: () => ' ',
        cell: (info) => (info.getValue() ? <Tag variant="danger">{lt('Deleted')}</Tag> : null),
    }),
];
